import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { DealStage, DealType, LeadSource } from '@/lib/types';

/**
 * Loads the three picker tables behind the deal form.
 *
 * Two decisions worth knowing:
 *
 * **`perPage: 100`, always.** The lists paginate like everything else and
 * default to 20, so a pipeline with more than 20 stages would silently show
 * only the first 20 — a dropdown that is quietly wrong is worse than one that
 * is obviously empty. `truncated` says when even 100 was not enough.
 *
 * **No `active` filter.** Fetching `?active=true` would give a clean create
 * dropdown but would drop the value an existing deal already holds if that
 * option has since been retired, which reads as "unassigned" and clears the
 * field on the next save. So everything is fetched once and the create/edit
 * distinction is made at render time with the documented rule:
 *
 * ```ts
 * options.filter((o) => o.isActive || o.id === currentValueId)
 * ```
 *
 * `selectablePickerOptions` below implements exactly that.
 *
 * **`failedBy` is per list, and that is the point of it.** `Promise.allSettled`
 * lets one table fail without blanking the other two, and for a long time the
 * rejected branch set nothing and told nobody: the dropdown rendered empty,
 * healthy-looking, next to its own "+ Add a new stage…" row. Pressing that is a
 * real POST into a `UNIQUE`-named table, so a read failure nobody reported
 * turned into a duplicate row nobody asked for.
 *
 * An aggregate flag would fix the silence and introduce a smaller lie — marking
 * three fields when one endpoint is down says two lists are unreadable that
 * were read perfectly well. `failed` is offered as the "anything at all?"
 * convenience; render `failedBy` on the control itself.
 */
/** One flag per list, for both `failedBy` and `truncatedBy`. */
export interface DealPickersByList {
  stages: boolean;
  dealTypes: boolean;
  leadSources: boolean;
}

const NONE: DealPickersByList = { stages: false, dealTypes: false, leadSources: false };

export function useDealPickers(enabled = true) {
  const [stages, setStages] = useState<DealStage[]>([]);
  const [dealTypes, setDealTypes] = useState<DealType[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  // Starts true when enabled so the first paint shows loading pickers rather
  // than empty ones, and nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [truncatedBy, setTruncatedBy] = useState<DealPickersByList>(NONE);
  const [failedBy, setFailedBy] = useState<DealPickersByList>(NONE);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const options = { perPage: 100 } as const;

    Promise.allSettled([
      api.listDealStages(options, controller.signal),
      api.listDealTypes(options, controller.signal),
      api.listLeadSources(options, controller.signal),
    ])
      .then(([stageResult, typeResult, sourceResult]) => {
        if (controller.signal.aborted) return;
        // Settled rather than all: one picker failing must not blank the other
        // two and take the whole form down with it.
        if (stageResult.status === 'fulfilled') {
          setStages(stageResult.value.data);
        } else {
          // The branch that was missing. Clearing the rows is not the fix on its
          // own — an empty list is exactly what the caller was already being
          // shown — so the list and the flag that explains it move together.
          setStages([]);
        }
        if (typeResult.status === 'fulfilled') {
          setDealTypes(typeResult.value.data);
        } else {
          setDealTypes([]);
        }
        if (sourceResult.status === 'fulfilled') {
          setLeadSources(sourceResult.value.data);
        } else {
          setLeadSources([]);
        }
        /*
         * A rejected list is not truncated, it is absent — `failedBy` is what
         * describes it, and claiming a page boundary as well would offer "there
         * are more" about a list that produced none.
         */
        setTruncatedBy({
          stages: stageResult.status === 'fulfilled' && stageResult.value.meta.totalPages > 1,
          dealTypes: typeResult.status === 'fulfilled' && typeResult.value.meta.totalPages > 1,
          leadSources: sourceResult.status === 'fulfilled' && sourceResult.value.meta.totalPages > 1,
        });
        setFailedBy({
          stages: stageResult.status === 'rejected',
          dealTypes: typeResult.status === 'rejected',
          leadSources: sourceResult.status === 'rejected',
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [enabled]);

  /*
   * A freshly created option is pushed straight into the list rather than
   * refetching. That is the whole point of the picker being a table: the
   * returned id is immediately usable as the deal's stageId, with no refresh.
   * Insertion respects the server's `sortOrder ASC, id ASC` so the new row
   * lands where the next fetch would put it.
   */
  const addStage = useCallback((created: DealStage) => {
    setStages((prev) => insertSorted(prev, created));
  }, []);
  const addDealType = useCallback((created: DealType) => {
    setDealTypes((prev) => insertSorted(prev, created));
  }, []);
  const addLeadSource = useCallback((created: LeadSource) => {
    setLeadSources((prev) => insertSorted(prev, created));
  }, []);

  return {
    stages,
    dealTypes,
    leadSources,
    loading,
    /**
     * Any of the three ran past `perPage: 100`. Kept as the aggregate it has
     * always been so existing callers are unaffected; prefer `truncatedBy`,
     * which can say WHICH list is cut short instead of marking all three.
     */
    truncated: truncatedBy.stages || truncatedBy.dealTypes || truncatedBy.leadSources,
    /** Which of the three ran past one page. */
    truncatedBy,
    /** Which of the three could not be read. Render this on the control. */
    failedBy,
    /** Any of the three. A convenience for a page-level notice, never a substitute. */
    failed: failedBy.stages || failedBy.dealTypes || failedBy.leadSources,
    addStage,
    addDealType,
    addLeadSource,
  };
}

/** Keeps a list in the server's curated order after a local insert. */
function insertSorted<T extends { id: number; sortOrder: number }>(list: T[], row: T): T[] {
  return [...list, row].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

/**
 * The options a dropdown may offer, given what the record currently holds.
 *
 * A retired option is hidden from new selections but kept when it is the
 * record's own value — otherwise an edit form silently reassigns a deal the
 * moment someone opens it.
 */
export function selectablePickerOptions<T extends { id: number; isActive: boolean }>(
  options: T[],
  currentValueId: number | null,
): T[] {
  return options.filter((option) => option.isActive || option.id === currentValueId);
}
